if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Add helper method to pause other videos
        this.pauseOtherVideos = (currentDeferredMedia) => {
          this.elements.viewer.querySelectorAll('.deferred-media[loaded]').forEach((otherDeferredMedia) => {
            if (otherDeferredMedia !== currentDeferredMedia) {
              const otherVideo = otherDeferredMedia.querySelector('video');
              const otherPosterButton = otherDeferredMedia.querySelector('.deferred-media__poster');
              if (otherVideo && !otherVideo.paused) {
                otherVideo.pause();
              }
              if (otherPosterButton) {
                otherPosterButton.style.display = 'flex';
              }
            }
          });
        };

        // Add click handler for deferred media unloading
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('click', (e) => {
            const deferredMedia = e.target.closest('.deferred-media');
            if (deferredMedia && deferredMedia.hasAttribute('loaded')) {
              const video = deferredMedia.querySelector('video');
              const posterButton = deferredMedia.querySelector('.deferred-media__poster');
              if (video && posterButton) {
                // Don't handle click if video is not ready
                if (video.readyState === 0 || !video.duration) {
                  return;
                }

                e.preventDefault();
                e.stopPropagation();

                // Add pause event handler if not already added
                if (!video.hasPlayPauseHandler) {
                  video.addEventListener('pause', () => {
                    posterButton.style.display = 'flex';
                  });
                  video.hasPlayPauseHandler = true;
                }

                if (video.paused) {
                  this.pauseOtherVideos(deferredMedia);
                  video.play();
                  posterButton.style.display = 'none';
                } else {
                  video.pause();
                  posterButton.style.display = 'flex';
                }
              }
            }
          });
        }

        // Auto-play the first product video, but defer it until the gallery
        // is actually in view and the page is idle. Skips if the user prefers
        // reduced motion. Without this guard a 1080p product video downloads
        // on every PDP load and fights for bandwidth with the rest of the page.
        if (this.elements.viewer) {
          const firstDeferredMedia = this.elements.viewer.querySelector('.deferred-media');
          if (firstDeferredMedia) {
            const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const startVideo = () => {
              firstDeferredMedia.loadContent(false);
              setTimeout(() => {
                const video = firstDeferredMedia.querySelector('video');
                const posterButton = firstDeferredMedia.querySelector('.deferred-media__poster');
                if (video && posterButton) {
                  posterButton.style.display = 'none';
                  video.play().catch(console.error);
                }
              }, 0);
            };

            if (!reducedMotion) {
              if ('IntersectionObserver' in window) {
                const obs = new IntersectionObserver(
                  ([entry]) => {
                    if (entry.isIntersecting) {
                      obs.disconnect();
                      const fire = () => startVideo();
                      if ('requestIdleCallback' in window) {
                        requestIdleCallback(fire, { timeout: 2000 });
                      } else {
                        setTimeout(fire, 800);
                      }
                    }
                  },
                  { rootMargin: '200px 0px' }
                );
                obs.observe(firstDeferredMedia);
              } else {
                setTimeout(startVideo, 1500);
              }
            }
          }
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      // Edited by NANO 5/2/2024
      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          deferredMedia.loadContent(false);

          // Force load and play the video
          const template = deferredMedia.querySelector('template');
          if (template) {
            const clone = template.content.cloneNode(true);
            const video = clone.querySelector('video');
            if (video) {
              video.setAttribute('autoplay', '');
              video.setAttribute('playsinline', '');

              // Get the poster button before appending video
              const posterButton = deferredMedia.querySelector('.deferred-media__poster');
              if (posterButton) {
                // Set up event handlers before appending video
                video.addEventListener('play', () => {
                  this.pauseOtherVideos(deferredMedia);
                  posterButton.style.display = 'none';
                });

                // Add pause event handler and mark it
                video.addEventListener('pause', () => {
                  posterButton.style.display = 'flex';
                });
                video.hasPlayPauseHandler = true;
              }

              // Now append the video
              deferredMedia.appendChild(clone);

              if (posterButton) {
                posterButton.style.display = 'none';

                // Initial play
                this.pauseOtherVideos(deferredMedia);
                video.play().catch(function (error) {
                  console.log('Video autoplay failed:', error);
                  if (posterButton) posterButton.style.display = 'flex';
                });
              }
            }
          }
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
